import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronDown, MessageSquare, Trash2, Users, Calendar, Wrench, CalendarPlus, CalendarClock, UserCog, RefreshCw, MoreHorizontal } from 'lucide-react';
import { lookupDmsCustomers } from '../services/api';
import { formatZonedDate, formatZonedDateTime, formatAppointmentDateTime } from '../services/dateService';
import ChatBox from './ChatBox';
import ManualAppointmentModal from './ManualAppointmentModal';
import EditLeadDetailsModal from './EditLeadDetailsModal';

// +14074900265 -> (407) 490-0265. Anything that isn't a 10-digit US number is
// shown as stored, so bad data stays visible instead of being silently mangled.
const formatPhone = (phoneNumber) => {
  if (!phoneNumber) return '';
  let digits = String(phoneNumber).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return phoneNumber;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// The backend uses "Customer Pending" as a placeholder until the customer's
// name is known; show that as an explicit unknown rather than as a name.
const PLACEHOLDER_NAMES = ['customer pending', ''];

// Floating, closeable chat widget that opens (bottom-right, like a
// support-chat bubble) when a lead is selected from the table — replaces
// the old always-visible details column / full-height slide-over drawer.
const ChatWidget = ({
  selectedLead,
  selectedLeadId,
  onClose,
  scrollToBottomOnLoad,
  onMessageSent,
  onDeleteLead,
  deletingLeadId,
  onCancelAppointment,
  cancelAppointmentError,
  onDismissCancelAppointmentError,
  onManualAppointmentAction,
  onUpdateLeadDetails,
  storeTimeZone,
  user,
  awaitingAiReplySince = null,
  onAiReplyWaitDone,
  rightOffsetPx = 0,
  isFocused = false,
  onFocusWidget,
  onWidthChange,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const chatBoxRef = useRef(null);
  const [isReloadingMessages, setIsReloadingMessages] = useState(false);
  const [dmsLookupResult, setDmsLookupResult] = useState(null);
  const [isDmsLookupLoading, setIsDmsLookupLoading] = useState(false);
  const [showDmsLookupModal, setShowDmsLookupModal] = useState(false);
  const [showManualAppointmentModal, setShowManualAppointmentModal] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  // AI on/off lives in ChatBox (it owns the fetch + toggle); ChatBox reports
  // it up here so the switch can sit in the widget header, next to "Last active".
  const [aiState, setAiState] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  useEffect(() => {
    if (!showMoreMenu) return undefined;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);


  const [panelSize, setPanelSize] = useState({ width: 440, height: 680 });
  const resizeStartRef = useRef(null);

  const handleResizeMove = useCallback((e) => {
    const start = resizeStartRef.current;
    if (!start) return;
    const minWidth = 340;
    const minHeight = 420;
    const maxWidth = window.innerWidth * 0.9;
    const maxHeight = window.innerHeight * 0.86;
    const dx = start.pointerX - e.clientX;
    const dy = start.pointerY - e.clientY;
    const nextWidth = Math.min(maxWidth, Math.max(minWidth, start.width + dx));
    const nextHeight = Math.min(maxHeight, Math.max(minHeight, start.height + dy));
    setPanelSize({ width: nextWidth, height: nextHeight });
  }, []);

  const handleResizeEnd = useCallback(() => {
    resizeStartRef.current = null;
    window.removeEventListener('mousemove', handleResizeMove);
    window.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    resizeStartRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      width: panelSize.width,
      height: panelSize.height,
    };
    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handleResizeEnd);
  }, [panelSize, handleResizeMove, handleResizeEnd]);

  // Safety cleanup if the widget unmounts mid-drag
  useEffect(() => () => {
    window.removeEventListener('mousemove', handleResizeMove);
    window.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd]);

  // Reopen fully whenever a different lead is selected
  useEffect(() => {
    setIsMinimized(false);
  }, [selectedLeadId]);

  useEffect(() => {
    onWidthChange?.(selectedLeadId, isMinimized ? 56 : panelSize.width);
  }, [selectedLeadId, panelSize.width, isMinimized, onWidthChange]);

  if (!selectedLead) return null;

  const hasRealName = !PLACEHOLDER_NAMES.includes((selectedLead.customerName || '').trim().toLowerCase());
  const displayName = hasRealName ? selectedLead.customerName : 'Unknown customer';

  const initials = (hasRealName ? selectedLead.customerName : '?')
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const vehicleLabel = selectedLead.vehicle
    ? `${selectedLead.vehicle.year} ${selectedLead.vehicle.make} ${selectedLead.vehicle.model}`
    : selectedLead.customerEmail;

  const hasActiveAppointment =
    selectedLead.appointment &&
    (selectedLead.appointment.status === 'BOOKED' || selectedLead.appointment.status === 'RESCHEDULED');

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const manualAction = selectedLead.manualAction;
  const canRunManualAction =
    isAdmin && manualAction && manualAction !== 'NONE';

  const MANUAL_ACTION_LABEL = {
    BOOK: 'Book now',
    RESCHEDULE: 'Reschedule',
    REBOOK: 'Re-book',
  };

  const getAppointmentStatusStyles = (status) => {
    switch (status) {
      case 'BOOKED':
        return {
          container: 'bg-green-50/60 border-green-100',
          label: 'text-green-800',
          badge: 'bg-white text-green-700 border-green-100',
          icon: 'text-green-500',
          button: 'bg-green-100 text-green-800 hover:bg-green-200'
        };
      case 'CANCELED':
        return {
          container: 'bg-red-50/60 border-red-100',
          label: 'text-red-800',
          badge: 'bg-white text-red-700 border-red-100',
          icon: 'text-red-500',
          button: 'bg-red-100 text-red-800 hover:bg-red-200'
        };
      case 'RESCHEDULED':
        return {
          container: 'bg-blue-50/60 border-blue-100',
          label: 'text-blue-800',
          badge: 'bg-white text-blue-700 border-blue-100',
          icon: 'text-blue-500',
          button: 'bg-blue-100 text-blue-800 hover:bg-blue-200'
        };
      default:
        return {
          container: 'bg-orange-50/60 border-orange-100',
          label: 'text-orange-800',
          badge: 'bg-white text-orange-700 border-orange-100',
          icon: 'text-orange-500',
          button: 'bg-orange-100 text-orange-800 hover:bg-orange-200'
        };
    }
  };

  const handleDmsLookup = async () => {
    setIsDmsLookupLoading(true);
    setShowDmsLookupModal(true);
    try {
      const result = await lookupDmsCustomers(selectedLead.id);
      setDmsLookupResult(result);
    } catch (error) {
      setDmsLookupResult({
        count: 0,
        phoneNumber: selectedLead.customerPhone,
        customers: [],
        message: error.message || 'DMS lookup failed',
      });
    } finally {
      setIsDmsLookupLoading(false);
    }
  };

  // Minimized: just a small launcher bubble, click to reopen
  if (isMinimized) {
    return (
      <button
        onClick={() => { onFocusWidget?.(selectedLeadId); setIsMinimized(false); }}
        style={{ right: 24 + rightOffsetPx }}
        className="fixed bottom-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 shadow-lg shadow-blue-500/30 flex items-center justify-center hover:scale-105 transition-transform"
title={`Reopen chat with ${displayName}`}
      >
        <MessageSquare size={22} className="text-white" />
      </button>
    );
  }

  return (
    <div
      onMouseDown={() => onFocusWidget?.(selectedLeadId)}
      className={`fixed bottom-6 ${isFocused ? 'z-50' : 'z-40'} min-w-[340px] min-h-[420px] max-w-[90vw] max-h-[86vh] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden`}
      style={{ right: 24 + rightOffsetPx, width: panelSize.width, height: panelSize.height }}
    >
      {/* Resize handle — drag from the top-left corner. The panel is
          anchored bottom-right, so dragging up/left grows it. */}
      <div
        onMouseDown={handleResizeStart}
        className="absolute top-0 left-0 w-5 h-5 z-50 cursor-nwse-resize flex items-start justify-start p-1"
        title="Drag to resize"
      >
        <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3 opacity-80">
          <path d="M2 10 10 2M2 6 6 2M2 2 2 2" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      {/* Widget header */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-5 pt-5 pb-4 flex-shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-white text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <div className={`text-base font-bold truncate ${hasRealName ? 'text-white' : 'text-white/80 italic'}`}>
                {displayName}
              </div>
              {vehicleLabel && (
                <div className="text-xs text-blue-100 truncate">{vehicleLabel}</div>
              )}
              {selectedLead.customerPhone && (
                <div className="text-xs text-blue-100 truncate">{formatPhone(selectedLead.customerPhone)}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => chatBoxRef.current?.reload()}
              disabled={isReloadingMessages}
              className="w-7 h-7 rounded-full flex items-center justify-center text-white/90 hover:bg-white/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              title="Reload messages — use this if a message you just sent isn't showing up"
            >
              <RefreshCw size={15} className={isReloadingMessages ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setIsMinimized(true)}
              className="w-7 h-7 rounded-full flex items-center justify-center text-white/90 hover:bg-white/20 transition-colors"
              title="Minimize"
            >
              <ChevronDown size={17} />
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full flex items-center justify-center text-white/90 hover:bg-white/20 transition-colors"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-2.5">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
            <span className="text-xs text-purple-100 truncate">
              Last active: {formatZonedDate(selectedLead.lastActivity, storeTimeZone)}
            </span>
          </span>
          {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && aiState && (
            <button
              type="button"
              role="switch"
              aria-checked={aiState.enabled}
              onClick={() => chatBoxRef.current?.toggleAi()}
              disabled={aiState.toggling}
              className={`flex-shrink-0 inline-flex items-center gap-2 h-6 pl-1 pr-2 rounded-full text-xs font-semibold transition-colors disabled:opacity-70 ${
                aiState.enabled
                  ? 'bg-white text-green-700 hover:bg-green-50'
                  : 'bg-white/15 text-white ring-1 ring-white/40 hover:bg-white/25'
              }`}
              title={aiState.enabled ? 'AI is replying to this customer - click to pause' : 'AI is paused for this customer - click to resume'}
            >
              <span
                className={`relative w-6 h-3.5 rounded-full transition-colors ${
                  aiState.enabled ? 'bg-green-500' : 'bg-white/40'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-2.5 h-2.5 rounded-full bg-white shadow transition-transform ${
                    aiState.enabled ? 'translate-x-2.5' : 'translate-x-0'
                  }`}
                />
              </span>
              {aiState.toggling ? 'Updating…' : 'AI'}
            </button>
          )}
        </div>
      </div>

      {/* Quick lead actions - the goal (booking) is the primary button,
          lookups are secondary, and the destructive action is tucked away. */}
      <div className="px-5 py-3 border-b border-gray-100 flex flex-col gap-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          {canRunManualAction && !selectedLead.appointment && (
            <button
              onClick={() => setShowManualAppointmentModal(true)}
              className="flex items-center gap-1.5 bg-purple-600 text-white text-xs font-semibold h-8 px-3 rounded-lg hover:bg-purple-700 transition-colors"
              title="Book this customer into Ztime"
            >
              <CalendarPlus size={14} />
              Book Appointment
            </button>
          )}

          {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
            <button
              onClick={() => setShowEditDetailsModal(true)}
              className="flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium h-8 px-3 rounded-lg hover:bg-gray-50 transition-colors"
              title="Manually fill in or correct customer, vehicle, service and transportation info - useful when N2 missed something or is off"
            >
              <UserCog size={14} className="text-gray-500" />
              Edit Details
            </button>
          )}

          {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
            <button
              onClick={handleDmsLookup}
              disabled={isDmsLookupLoading}
              className="flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium h-8 px-3 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              title="Look this phone number up in the dealer's DMS"
            >
              {isDmsLookupLoading ? (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-gray-500" />
              ) : (
                <Users size={14} className="text-gray-500" />
              )}
              DMS Lookup
            </button>
          )}

          <div className="relative ml-auto" ref={moreMenuRef}>
            <button
              onClick={() => setShowMoreMenu((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={showMoreMenu}
              aria-label="More actions"
              className="flex items-center justify-center h-8 w-8 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              title="More actions"
            >
              <MoreHorizontal size={16} />
            </button>
            {showMoreMenu && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30"
              >
                <button
                  role="menuitem"
                  onClick={() => {
                    setShowMoreMenu(false);
                    onDeleteLead(selectedLead.id);
                  }}
                  disabled={deletingLeadId === selectedLead.id}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  {deletingLeadId === selectedLead.id ? 'Deleting…' : 'Delete lead'}
                </button>
              </div>
            )}
          </div>
        </div>

        {selectedLead.appointment && (() => {
          const styles = getAppointmentStatusStyles(selectedLead.appointment.status);
          const isCanceled = selectedLead.appointment.status === 'CANCELED';
          return (
            <div className={`flex items-center gap-2 flex-wrap ${styles.container} border rounded-lg px-3 py-2 ${isCanceled ? 'opacity-60' : ''}`}>
              <span className={`text-xs font-semibold ${styles.label}`}>Appointment</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles.badge}`}>
                {selectedLead.appointment.status}
              </span>

              {selectedLead.appointment.confirmedDateTime ? (
                <span
                  className={`inline-flex items-center gap-1 text-xs text-gray-600 bg-white px-2 py-1 rounded-full border ${styles.icon} ${isCanceled ? 'line-through' : ''}`}
                  title="Date/time confirmed in the scheduler"
                >
                  <Calendar size={11} className={styles.icon} />
                  Confirmed {formatAppointmentDateTime(selectedLead.appointment.confirmedDateTime)}
                </span>
              ) : selectedLead.appointment.requestedDateTime && (
                <span className={`inline-flex items-center gap-1 text-xs text-gray-600 bg-white px-2 py-1 rounded-full border ${styles.icon} ${isCanceled ? 'line-through' : ''}`}>
                  <Calendar size={11} className={styles.icon} />
                  {formatAppointmentDateTime(selectedLead.appointment.requestedDateTime)}
                </span>
              )}

              {selectedLead.appointment.pendingChange && selectedLead.appointment.requestedDateTime && (
                <span
                  className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-full border border-amber-200"
                  title="Customer asked for this slot - the scheduler has not confirmed it"
                >
                  <CalendarClock size={11} />
                  Requested {formatAppointmentDateTime(selectedLead.appointment.requestedDateTime)}
                </span>
              )}

              {selectedLead.appointment.service && (
                <span className={`inline-flex items-center gap-1 text-xs text-gray-600 bg-white px-2 py-1 rounded-full border ${styles.icon} ${isCanceled ? 'line-through' : ''}`}>
                  <Wrench size={11} className={styles.icon} />
                  {selectedLead.appointment.service}
                </span>
              )}

              <div className="ml-auto flex items-center gap-1.5">
                {canRunManualAction && (
                  <button
                    onClick={() => setShowManualAppointmentModal(true)}
                    className="flex items-center gap-1 bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors"
                    title={
                      manualAction === 'RESCHEDULE'
                        ? 'Move this Ztime appointment to a new date/time'
                        : manualAction === 'REBOOK'
                        ? 'Clear the canceled Ztime references and book a new appointment'
                        : 'Send this appointment to Ztime now'
                    }
                  >
                    {manualAction === 'RESCHEDULE' || manualAction === 'REBOOK' ? <CalendarClock size={12} /> : <CalendarPlus size={12} />}
                    {MANUAL_ACTION_LABEL[manualAction]}
                  </button>
                )}

                {hasActiveAppointment && isAdmin && (
                  <button
                    onClick={() => onCancelAppointment(selectedLead.id)}
                    className={`flex items-center gap-1 ${styles.button} text-xs font-semibold px-2.5 py-1 rounded-full transition-colors`}
                  >
                    <X size={12} />
                    Cancel
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {cancelAppointmentError && cancelAppointmentError.leadId === selectedLead.id && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
            <span className="flex-1">Failed to cancel appointment: {cancelAppointmentError.message}</span>
            <button
              onClick={onDismissCancelAppointmentError}
              className="text-red-500 hover:text-red-700 font-semibold shrink-0"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Conversation */}
      <div className="flex-1 overflow-hidden">
        <ChatBox
          ref={chatBoxRef}
          key={selectedLeadId}
          leadId={selectedLeadId}
          scrollToBottomOnLoad={scrollToBottomOnLoad}
          selectedLead={selectedLead}
          onMessageSent={onMessageSent}
          storeTimeZone={storeTimeZone}
          onReloadStateChange={setIsReloadingMessages}
          onAiStateChange={setAiState}
          awaitingAiReplySince={awaitingAiReplySince}
          onAiReplyWaitDone={onAiReplyWaitDone}
        />
      </div>

      {/* DMS Lookup Modal */}
      {showDmsLookupModal && (
        <div className="absolute inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">DMS Customer Lookup</h4>
              <button
                onClick={() => setShowDmsLookupModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4">
              {isDmsLookupLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-600" />
                </div>
              ) : dmsLookupResult ? (
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Phone:</span> {dmsLookupResult.phoneNumber}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Customers found:</span>{' '}
                    <span className="text-lg font-bold text-indigo-600">{dmsLookupResult.count}</span>
                  </div>
                  {dmsLookupResult.message && (
                    <div className="text-xs text-gray-500">{dmsLookupResult.message}</div>
                  )}
                  {dmsLookupResult.customers && dmsLookupResult.customers.length > 0 && (
                    <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-md">
                      {dmsLookupResult.customers.map((customer, index) => (
                        <li key={index} className="px-3 py-2 text-xs text-gray-700">
                          <div className="font-medium">
                            {customer.firstName} {customer.lastName}
                          </div>
                          <div className="text-gray-500">
                            CustNo: {customer.custNo} · {customer.cellular}
                          </div>
                          {customer.vehicles && customer.vehicles.length > 0 && (
                            <div className="mt-2 pl-2 border-l-2 border-indigo-100 space-y-1">
                              <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                                Vehicles
                              </div>
                              {customer.vehicles.map((vehicle, vIndex) => (
                                <div key={vIndex} className="text-[11px] text-gray-600">
                                  {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                                  {vehicle.vin && (
                                    <span className="text-gray-400 ml-1">· VIN: {vehicle.vin}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex justify-end px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDmsLookupModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualAppointmentModal && (
        <ManualAppointmentModal
          lead={selectedLead}
          onClose={() => setShowManualAppointmentModal(false)}
          onSubmit={onManualAppointmentAction}
        />
      )}

      {showEditDetailsModal && (
        <EditLeadDetailsModal
          lead={selectedLead}
          onClose={() => setShowEditDetailsModal(false)}
          onSubmit={onUpdateLeadDetails}
        />
      )}
    </div>
  );
};

export default ChatWidget;
